import React from "react";
import { Autocomplete, Box, CircularProgress, TextField, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EventFilters from "./EventFilters";
import { EventToolBarProps } from "../interfaces/eventToolBar";
import themestyle from "../theme";

const EventToolBar: React.FC<EventToolBarProps> = ({
  searchQuery,
  onSearchQueryChange,
  instructorValue,
  dayValue,
  amountValue,
  bookingValue,
  onInstructorChange,
  onDayChange,
  onAmountChange,
  onBookingChange,
  eventOptions,
  eventOptionsLoading,
  instructorOptions,
  hideBorder = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 1 : 2,
        px: 0,
        py: isMobile ? 1 : 2,
        backgroundColor: "transparent",
        borderBottom: hideBorder ? "none" : "1px solid #E5E7EB",
        borderRadius: 0,
        mb: { xs: 3, md: 5 },
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ flex: 1, width: "100%", minWidth: 0 }}>
        <Autocomplete
          size={isMobile ? "small" : "medium"}
          options={eventOptions || []}
          value={searchQuery || null}
          onChange={(_, newValue) => onSearchQueryChange(newValue || "")}
          loading={eventOptionsLoading}
          loadingText="Loading events..."
          noOptionsText={eventOptionsLoading ? "Loading..." : "No events found"}
          disableClearable={false}
          forcePopupIcon={isMobile ? !searchQuery : true}
          sx={{
            width: isMobile ? "95%" : "100%",
            mx: isMobile ? "auto" : 0,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label=""
              placeholder="Filter by event"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {eventOptionsLoading ? (
                      <CircularProgress color="inherit" size={20} sx={{ color: themestyle.colors.activeGreen }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
              sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "32px",
                backgroundColor: themestyle.colors.white,
                fontSize: isMobile ? 13 : 14,
                minHeight: isMobile ? 45 : 52,
                px: isMobile ? 1 : 2,
                "& fieldset": {
                  borderWidth: "1px",
                  borderColor: themestyle.colors.bordercolor,
                },
                "&:hover fieldset": {
                  borderColor:themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
                "&.Mui-focused fieldset": {
                  borderColor: themestyle.colors.activeGreen,
                  borderWidth: "1px",
                },
              },
              "& .MuiInputBase-input": {
                height: isMobile ? "25px" : "auto",
              }
            }}
            />
          )}
          slotProps={{
            listbox: {
              sx: {
                fontSize: isMobile ? 11 : 14,
                '& .MuiAutocomplete-option': {
                  padding: isMobile ? '4px 8px' : '6px 16px',
                  minHeight: isMobile ? 'auto' : undefined,
                },
              },
            },
            popper: isMobile ? {
              placement: "bottom-end",
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, 4],
                  },
                },
              ],
            } : undefined,
          }}
        />
      </Box>
      {onInstructorChange && (
        <Box sx={{ flex: 2, display: "flex", gap: isMobile ? 0.25 : 2, width: "100%", minWidth: 0 }}>
          <EventFilters
            instructorValue={instructorValue || null}
            dayValue={dayValue || null}
            amountValue={amountValue || null}
            bookingValue={bookingValue || null}
            onInstructorChange={onInstructorChange}
            onDayChange={onDayChange || (() => {})}
            onAmountChange={onAmountChange}
            onBookingChange={onBookingChange}
            instructorOptions={instructorOptions}
            hideDayFilter={!onDayChange}
          />
        </Box>
      )}
    </Box>
  );
};
export default EventToolBar;
